import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useRecipeStockItemsKeys } from "./useRecipeStockItemsKeys";
import {
  getRecipeStockItems,
  deleteRecipeStockItem,
  createRecipeStockItem,
  getRecipeStockItemDetails,
  updateRecipeStockItem,
} from "../api/RecipeStockItemsApi";
import { errorMessage, successMessage } from "../../../utils/helper";

export const useRecipeStockItemsQueries = ({
  recipeId,
  stockId,
  resetDeleteInfo,
}) => {
  const { recipeStockItemsKeys } = useRecipeStockItemsKeys();
  const queryClient = useQueryClient();

  const recipeStockItemsQuery = useQuery({
    queryKey: recipeStockItemsKeys.list(recipeId),
    queryFn: () => getRecipeStockItems(recipeId),
    enabled: !!recipeId && recipeId > 0,
  });

  const recipeStockItemDetailsQuery = useQuery({
    queryKey: recipeStockItemsKeys.detail(recipeId, stockId),
    queryFn: () => getRecipeStockItemDetails({ recipeId, stockId }),
    enabled: !!recipeId && !!stockId && recipeId > 0 && stockId > 0,
  });

  const mutateDeleteStockItem = useMutation({
    mutationFn: deleteRecipeStockItem,
    onError: (error) => {
      errorMessage(
        error?.response?.data?.message || "Failed to delete stock item",
      );
    },
    onSuccess: (res) => {
      if (res.success) {
        queryClient.invalidateQueries({
          queryKey: recipeStockItemsKeys.list(recipeId),
        });
        successMessage(res.message || "Stock item deleted successfully");
        resetDeleteInfo();
      } else if (res.success === false) {
        errorMessage(res.message);
      }
    },
  });

  const mutateCreateStockItem = useMutation({
    mutationFn: createRecipeStockItem,
    onError: (error) => {
      errorMessage(
        error?.response?.data?.message || "Failed to create stock item",
      );
    },
    onSuccess: (res) => {
      if (res.success) {
        queryClient.invalidateQueries({
          queryKey: recipeStockItemsKeys.list(recipeId),
        });
        successMessage(res.message || "Stock item created successfully");
      } else if (res.success === false) {
        errorMessage(res.message);
      }
    },
  });

  const mutateUpdateStockItem = useMutation({
    mutationFn: updateRecipeStockItem,
    onError: (error) => {
      errorMessage(
        error?.response?.data?.message || "Failed to update stock item",
      );
    },
    onSuccess: (res) => {
      if (res.success) {
        queryClient.invalidateQueries({
          queryKey: recipeStockItemsKeys.list(recipeId),
        });
        queryClient.invalidateQueries({
          queryKey: recipeStockItemsKeys.detail(recipeId, stockId),
        });
        successMessage(res.message || "Stock item updated successfully");
      } else if (res.success === false) {
        errorMessage(res.message);
      }
    },
  });

  return {
    recipeStockItemsQuery,
    recipeStockItemDetailsQuery,
    mutateDeleteStockItem,
    mutateCreateStockItem,
    mutateUpdateStockItem,
  };
};
